import { useEffect } from "react";
import type { BoardToolId } from "../editor/types";
import { toolIdForKeyCode } from "../editor/boardKeybindings";

type Options = {
  enabled: boolean;
  activeTool: BoardToolId;
  onToolChange: (tool: BoardToolId) => void;
  selectedIds: string[];
  onDeleteSelection?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onDuplicate?: () => void;
  onCopySelection?: () => void;
  onPaste?: () => void;
  onZOrderForward?: () => void;
  onZOrderBackward?: () => void;
  onGroupSelection?: () => void;
  onUngroupSelection?: () => void;
  onImportPdf?: () => void;
  /** Сдвиг выделения стрелками: шаг в единицах доски. */
  onNudgeSelection?: (dx: number, dy: number) => void;
  /** Сменить вид линии у пера: сплошная → пунктир → точки. */
  onCyclePenDash?: () => void;
  /** Enter на одном выделенном объекте — открыть правку его текста. */
  onEditSelection?: (id: string) => void;
  /** Ctrl+Alt+C / Ctrl+Alt+V — копировать и вставить стиль, как в Miro. */
  onCopyStyle?: () => void;
  onPasteStyle?: () => void;
  /** G — включить или выключить прилипание прямых к горизонтали и вертикали. */
  onToggleLineAxisSnap?: () => void;
};

/** Шаг стрелок: обычный — точная подгонка, с Shift — быстрый сдвиг. */
const NUDGE_STEP = 1;
const NUDGE_STEP_FAST = 10;

/** Клавиша вида линии. «D» — dash, и свободна: инструменты её не занимают. */
const PEN_DASH_CODE = "KeyD";

/** Клавиша прилипания прямых: «G» — grid/горизонталь, инструментами не занята. */
const LINE_AXIS_SNAP_CODE = "KeyG";

const ARROW_DELTAS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

/** Физическая клавиша → инструмент (работает и на русской раскладке). */
function toolIdForCode(code: string): BoardToolId | null {
  return toolIdForKeyCode(code);
}

const TEXT_INPUT_TYPES = new Set([
  "text",
  "search",
  "email",
  "password",
  "url",
  "tel",
  "number",
  "date",
  "datetime-local",
  "month",
  "week",
  "time",
  "",
]);

/** Поле, где Delete/Backspace должны редактировать текст, а не удалять объект. */
function isTextEditingElement(el: HTMLElement): boolean {
  if (el.isContentEditable) return true;
  if (el.closest("[contenteditable='true'], .wb-text-overlay-editor, .wb-board-help, .cm-editor")) {
    return true;
  }
  const tag = el.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = ((el as HTMLInputElement).type || "text").toLowerCase();
    return TEXT_INPUT_TYPES.has(type);
  }
  return false;
}

/**
 * Фокус на UI аудио (плеер / «слушать вместе») — не набор текста.
 * Раньше checkbox/range блокировали Delete после прослушивания.
 */
function isBoardMediaChromeFocus(el: HTMLElement): boolean {
  if (el.tagName === "AUDIO") return true;
  return Boolean(el.closest(".wb-audio-card, .wb-scene-obj--audio, .wb-audio-card__player"));
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (typeof document !== "undefined") {
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      if (isBoardMediaChromeFocus(active) && !isTextEditingElement(active)) return false;
      if (isTextEditingElement(active)) return true;
    }
  }
  if (!(target instanceof HTMLElement)) return false;
  if (isBoardMediaChromeFocus(target) && !isTextEditingElement(target)) return false;
  return isTextEditingElement(target);
}

function blurBoardMediaChrome(): void {
  const active = document.activeElement;
  if (active instanceof HTMLElement && isBoardMediaChromeFocus(active)) {
    active.blur();
  }
}

export function useBoardToolShortcuts({
  enabled,
  activeTool,
  onToolChange,
  selectedIds,
  onDeleteSelection,
  onUndo,
  onRedo,
  onDuplicate,
  onCopySelection,
  onPaste: _onPaste,
  onZOrderForward,
  onZOrderBackward,
  onGroupSelection,
  onUngroupSelection,
  onImportPdf,
  onNudgeSelection,
  onCyclePenDash,
  onEditSelection,
  onCopyStyle,
  onPasteStyle,
  onToggleLineAxisSnap,
}: Options) {
  void _onPaste;
  useEffect(() => {
    if (!enabled) return;

    function onKey(ev: KeyboardEvent) {
      if (isTypingTarget(ev.target)) return;
      // Стиль — до проверки Alt: сочетание как раз с ним.
      if ((ev.ctrlKey || ev.metaKey) && ev.altKey && !ev.shiftKey) {
        if (ev.code === "KeyC" && onCopyStyle && selectedIds.length > 0) {
          ev.preventDefault();
          ev.stopPropagation();
          onCopyStyle();
          return;
        }
        if (ev.code === "KeyV" && onPasteStyle) {
          ev.preventDefault();
          ev.stopPropagation();
          onPasteStyle();
          return;
        }
      }
      if (ev.altKey) return;

      const mod = ev.ctrlKey || ev.metaKey;
      // code (KeyZ) — не зависит от русской раскладки; key ломает Ctrl+Z/C/V
      const code = ev.code;

      if (mod && code === "KeyZ" && !ev.shiftKey && onUndo) {
        ev.preventDefault();
        ev.stopPropagation();
        onUndo();
        return;
      }
      if (mod && ((code === "KeyY" && onRedo) || (code === "KeyZ" && ev.shiftKey && onRedo))) {
        ev.preventDefault();
        ev.stopPropagation();
        onRedo();
        return;
      }

      if (
        mod &&
        code === "KeyG" &&
        ev.shiftKey &&
        selectedIds.length > 0 &&
        onUngroupSelection
      ) {
        ev.preventDefault();
        onUngroupSelection();
        return;
      }
      if (
        mod &&
        code === "KeyG" &&
        !ev.shiftKey &&
        selectedIds.length > 1 &&
        onGroupSelection
      ) {
        ev.preventDefault();
        onGroupSelection();
        return;
      }

      if (mod && code === "KeyD" && selectedIds.length > 0 && onDuplicate) {
        ev.preventDefault();
        onDuplicate();
        return;
      }
      // Copy: не preventDefault — иначе не сработает событие "copy" и OS-буфер не обновится.
      // Данные пишутся в window "copy" (clipboardData.setData). Internal — на всякий случай здесь.
      if (mod && code === "KeyC" && selectedIds.length > 0 && onCopySelection) {
        onCopySelection();
        return;
      }
      // Ctrl+V не перехватываем здесь: иначе preventDefault убивает paste со скрином.
      // Вставка картинок и объектов доски — в обработчике события "paste".

      if (ev.key === "[" && selectedIds.length > 0 && onZOrderBackward) {
        ev.preventDefault();
        onZOrderBackward();
        return;
      }
      if (ev.key === "]" && selectedIds.length > 0 && onZOrderForward) {
        ev.preventDefault();
        onZOrderForward();
        return;
      }

      if (mod && ev.shiftKey && code === "KeyP" && onImportPdf) {
        ev.preventDefault();
        onImportPdf();
        return;
      }

      // Стрелки двигают выделение: точная подгонка там, где мышью не попасть.
      const arrow = ARROW_DELTAS[ev.key];
      if (arrow && !mod && selectedIds.length > 0 && onNudgeSelection) {
        ev.preventDefault();
        ev.stopPropagation();
        const step = ev.shiftKey ? NUDGE_STEP_FAST : NUDGE_STEP;
        onNudgeSelection(arrow[0] * step, arrow[1] * step);
        return;
      }

      if (!mod) {
        const toolId = toolIdForCode(code);
        if (toolId) {
          ev.preventDefault();
          onToolChange(toolId);
          return;
        }
        // Вид линии — только когда в руке перо, и только если эту клавишу
        // не забрал себе инструмент: раскладку клавиш можно менять.
        if (code === PEN_DASH_CODE && activeTool === "pen" && onCyclePenDash) {
          ev.preventDefault();
          onCyclePenDash();
          return;
        }
        // Работает при любом инструменте и даже посреди штриха с Shift.
        if (code === LINE_AXIS_SNAP_CODE && onToggleLineAxisSnap) {
          ev.preventDefault();
          onToggleLineAxisSnap();
          return;
        }
      }

      // Enter — как в Miro: открыть надпись для правки, курсор в конце.
      if (
        !mod &&
        !ev.shiftKey &&
        ev.key === "Enter" &&
        selectedIds.length === 1 &&
        onEditSelection &&
        // Открыто окно поверх доски — Enter его, а не надписи.
        !document.querySelector('dialog[open], [aria-modal="true"]')
      ) {
        ev.preventDefault();
        ev.stopPropagation();
        onEditSelection(selectedIds[0]!);
        return;
      }

      if (
        (ev.key === "Delete" ||
          ev.key === "Backspace" ||
          code === "Delete" ||
          code === "Backspace") &&
        selectedIds.length > 0 &&
        onDeleteSelection
      ) {
        // После play фокус на <audio>/checkbox — раньше isTypingTarget глушил Delete.
        ev.preventDefault();
        ev.stopPropagation();
        blurBoardMediaChrome();
        onDeleteSelection();
      }
    }

    // capture: раньше других обработчиков (popover/help), пока фокус на доске
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [
    enabled,
    onToolChange,
    selectedIds,
    onDeleteSelection,
    onUndo,
    onRedo,
    onDuplicate,
    onCopySelection,
    onZOrderForward,
    onZOrderBackward,
    onGroupSelection,
    onUngroupSelection,
    onImportPdf,
    onNudgeSelection,
    activeTool,
    onCyclePenDash,
    onEditSelection,
    onCopyStyle,
    onPasteStyle,
    onToggleLineAxisSnap,
  ]);
}
